import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Add,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TestDataService } from '@teamexos/test-data-service';
import { debounce, sum } from 'lodash';
import React, { useState } from 'react';
import { Button } from 'react-admin';
import { Controller, useForm } from 'react-hook-form';
import {
  AdminGetUserByEmailDocument,
  AdminManyClientsDocument,
} from '../../../graphql/types';
import { ResourceLink } from '../../reusable/ResourceLink';
import { ActionLogPanel } from './ActionLogPanel';
import { ClientMembersList } from './ClientMembersList';
import { DemoInstructions } from './DemoInstructions';
import {
  SALES_DEMO_COACH_DATA,
  SALES_DEMO_EMPLOYERS,
} from './data/salesDemoData';
import { MemberActionButton } from './MemberActionButton';
import { useSalesDemoActions } from './useSalesDemoActions';
import {
  useActionLog,
  useRequiredClientMembers,
  useTestDataService,
} from './utils';

const requiredClientIds = SALES_DEMO_EMPLOYERS.map((e) => e.id);

interface CreateSalesDemoFields {
  membersToCreate: string[];

  coachEmail: string;
  coachFirstName: string;
  coachLastName: string;
  memberCount: string;
}

interface CreateSalesDemoProps {}

export const CreateSalesDemo: React.FC<CreateSalesDemoProps> = () => {
  useTestDataService();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);
  const actionLog = useActionLog();
  const {
    logAction,
    memberCreationStatus,
    setMemberCreationStatus,
    memberCreationProgress,
    setMemberCreationProgress,
    errors,
    setErrors,
  } = actionLog;

  const [
    getCoachByEmail,
    {
      data: existingCoachData,
      called: coachSearched,
      loading: loadingCoachByEmail,
      refetch: refetchCoachByEmail,
    },
  ] = useLazyQuery(AdminGetUserByEmailDocument);
  const existingCoach = existingCoachData?.data;
  const existingCoachClients =
    existingCoach?.coachData?.coachClients.edges || [];

  const {
    data: requiredClientCoachUsers,
    refetch: refetchRequiredClientMembers,
  } = useRequiredClientMembers(requiredClientIds, existingCoach?.id);

  const { data: requiredClientsData } = useQuery(AdminManyClientsDocument, {
    variables: {
      ids: requiredClientIds,
    },
  });
  const requiredClients = requiredClientsData?.data || [];

  const {
    isMutating,
    addCoachClient,
    createStubUsersForClient,
    createPredefinedMembersForClient,
    clearMembersForClient,
  } = useSalesDemoActions({
    existingCoach,
    requiredClients,
    actionLog,
    refetchRequiredClientMembers,
  });

  const methods = useForm<CreateSalesDemoFields>({
    defaultValues: {
      coachEmail: '',
      coachFirstName: '',
      coachLastName: '',
    },
  });

  const handleSubmit = async (values: CreateSalesDemoFields) => {
    if (existingCoach) {
      return;
    }
    setErrors([]);
    setMemberCreationProgress([]);
    setMemberCreationStatus([]);

    // The `existingCoach` above can be stale if a debounced lookup is still in
    // flight when the form is submitted (e.g. pressing Enter). Run an
    // authoritative, awaited lookup and bail if a coach already exists, so we
    // never recreate (and rename) an existing coach.
    fetchAndSetCoach.cancel();
    if (values.coachEmail) {
      const { data } = await getCoachByEmail({
        variables: { email: values.coachEmail },
      });
      if (data?.data) {
        return;
      }
    }

    const coach = await logAction('Creating coach', () =>
      TestDataService.createUser({
        email: values.coachEmail,
        firstName: values.coachFirstName,
        lastName: values.coachLastName,
        isCoach: true,
        isE2E: false,
        coachData: SALES_DEMO_COACH_DATA,
      }),
    );

    if (!coach) {
      setErrors((p) => [...p, 'Failed to create coach']);
      return;
    }

    // The email may have been generated server-side; reflect the coach we
    // actually created and load their client table.
    methods.setValue('coachEmail', coach.email);
    await refetchCoachByEmail({ email: coach.email });
  };

  const totalPredefinedMemberCount = sum(
    requiredClientCoachUsers.map(
      (client) => client.predefinedUsers?.count ?? 0,
    ),
  );
  const totalStubUsersCount = sum(
    requiredClientCoachUsers.map((client) => client.stubUsers?.count ?? 0),
  );

  const fetchAndSetCoach = debounce(async (email: string) => {
    const { data } = await getCoachByEmail({
      variables: {
        email,
      },
    });
    methods.setValue('coachFirstName', data?.data?.firstName || '');
    methods.setValue('coachLastName', data?.data?.lastName || '');
  }, 500);

  const handleRefresh = async () => {
    setMemberCreationProgress([]);
    setMemberCreationStatus([]);
    setErrors([]);
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchRequiredClientMembers(),
        refetchCoachByEmail(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Box display="flex" flexDirection="row" alignItems="start">
      <Box display="flex" flexDirection="column" gap={2} width="100%">
        <Box>
          <Typography variant="h6">Sales Demo Tool</Typography>
          <Typography variant="body2" color="text.secondary">
            Set up a coach with demo members across the three sales-demo clients
            so you can present the product with realistic data. Enter a coach
            email to begin — an existing coach loads automatically, or you can
            create a new one. Once a coach is linked to a client, add demo or
            stub members for that client.
          </Typography>
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="start"
          sx={{ mb: 2 }}
        >
          <Box
            component="form"
            onSubmit={methods.handleSubmit(handleSubmit)}
            display="flex"
            flexDirection="column"
            alignItems="start"
            width="40%"
            gap={2}
          >
            <Controller
              control={methods.control}
              name="coachEmail"
              render={({ field }) => (
                <TextField
                  onChange={(e) => {
                    field.onChange(e);
                    fetchAndSetCoach(e.target.value);
                  }}
                  name={field.name}
                  value={field.value}
                  fullWidth
                  placeholder="Leave blank for generated email, or enter one using @testuser.ex-os.net"
                  label="Coach Email"
                  InputProps={{
                    endAdornment: loadingCoachByEmail ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              )}
            />
            {coachSearched && !loadingCoachByEmail && existingCoach && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>
                  {existingCoach.firstName} {existingCoach.lastName}
                </Typography>
                <ResourceLink name="User" id={existingCoach.id} type="show">
                  View
                </ResourceLink>
                <ResourceLink name="User" id={existingCoach.id} type="edit">
                  Edit
                </ResourceLink>
              </Box>
            )}
            {coachSearched && !loadingCoachByEmail && !existingCoach && (
              <>
                <Typography fontSize="small">
                  No coach found for this email. Enter a name to create one.
                </Typography>
                <Controller
                  control={methods.control}
                  name="coachFirstName"
                  render={({ field }) => (
                    <TextField
                      onChange={field.onChange}
                      name={field.name}
                      value={field.value}
                      fullWidth
                      label="Coach First Name"
                    />
                  )}
                />
                <Controller
                  control={methods.control}
                  name="coachLastName"
                  render={({ field }) => (
                    <TextField
                      onChange={field.onChange}
                      name={field.name}
                      value={field.value}
                      fullWidth
                      label="Coach Last Name"
                    />
                  )}
                />
                <Button
                  type="submit"
                  disabled={isMutating || loadingCoachByEmail}
                >
                  <Add />
                  create coach
                </Button>
              </>
            )}
          </Box>
          <Tooltip title="Refresh the data shown below">
            <span>
              <Button
                aria-label="Refresh"
                onClick={handleRefresh}
                disabled={isMutating}
                loading={isRefreshing}
              >
                <Refresh />
              </Button>
            </span>
          </Tooltip>
        </Box>

        {existingCoach && (
          <Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Predefined Members</TableCell>
                  <TableCell>Stub Members</TableCell>
                  <TableCell>Manage Predefined Members</TableCell>
                  <TableCell>Manage Stub Members</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requiredClients.map((requiredClient) => {
                  const clientCoachUsers = requiredClientCoachUsers.find(
                    (c) => c.clientId === requiredClient.id,
                  );
                  const predefinedMemberCount =
                    clientCoachUsers?.predefinedUsers?.count || 0;
                  const stubUsersCount =
                    clientCoachUsers?.stubUsers?.count || 0;
                  const members =
                    clientCoachUsers?.predefinedUsers?.edges?.map(
                      (edge) => edge.node,
                    ) ?? [];
                  const isConnected = existingCoachClients.some(
                    (coachClient) => requiredClient.id === coachClient.node.id,
                  );
                  // Only allow expanding once members exist for this coach at
                  // this client.
                  const canExpand = members.length > 0;
                  const isExpanded = expandedClientId === requiredClient.id;
                  return (
                    <React.Fragment key={requiredClient.id}>
                      <TableRow>
                        <TableCell>{requiredClient.displayName}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {predefinedMemberCount}
                            {canExpand ? (
                              <IconButton
                                aria-label="Show demo members"
                                size="small"
                                onClick={() =>
                                  setExpandedClientId((prev) =>
                                    prev === requiredClient.id
                                      ? null
                                      : requiredClient.id,
                                  )
                                }
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>{stubUsersCount}</TableCell>
                        {isConnected ? (
                          <>
                            <TableCell>
                              <MemberActionButton
                                count={predefinedMemberCount}
                                loading={isMutating}
                                createLabel="demo members"
                                onCreate={() =>
                                  createPredefinedMembersForClient(
                                    requiredClient.id,
                                  )
                                }
                                onClear={() =>
                                  clearMembersForClient({
                                    clientId: requiredClient.id,
                                    isStubUser: false,
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <MemberActionButton
                                count={stubUsersCount}
                                loading={isMutating}
                                createLabel="stub members"
                                onCreate={() =>
                                  createStubUsersForClient(requiredClient.id)
                                }
                                onClear={() =>
                                  clearMembersForClient({
                                    clientId: requiredClient.id,
                                    isStubUser: true,
                                  })
                                }
                              />
                            </TableCell>
                          </>
                        ) : (
                          <TableCell colSpan={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">
                                Connect this client before creating demo
                                members.
                              </Typography>
                              <Button
                                type="button"
                                label="Connect client"
                                variant="outlined"
                                onClick={() =>
                                  addCoachClient(requiredClient.id)
                                }
                                loading={isMutating}
                              >
                                <Add />
                              </Button>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                      {canExpand ? (
                        <TableRow>
                          <TableCell sx={{ py: 0 }} colSpan={5}>
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <ClientMembersList members={members} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
                <TableRow>
                  <TableCell>Total</TableCell>
                  <TableCell>{totalPredefinedMemberCount}</TableCell>
                  <TableCell>{totalStubUsersCount}</TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}

        <Box
          bgcolor="#F0F0F0"
          width="60%"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            borderRadius: 2,
            gap: 2,
          }}
        >
          <ActionLogPanel
            errors={errors}
            status={memberCreationStatus}
            progress={memberCreationProgress}
          />
        </Box>

        <DemoInstructions />
      </Box>
    </Box>
  );
};
